import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Paper, Title, TextInput, PasswordInput, Button, Alert, Stack, Text, Center, Loader } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { authApi } from '../api/client';
import { useAuthStore } from '../store/authSlice';
import { PageContainer } from '../components/PageContainer';

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login: storeLogin, isAuthenticated } = useAuthStore();
  const [form, setForm] = useState({ login: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [telegramAuthInProgress, setTelegramAuthInProgress] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Check for Telegram WebApp on mount
  useEffect(() => {
    const tg = (window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp;
    if (tg?.initData) {
      handleTelegramAuth(tg.initData);
    }
  }, []);

  const handleTelegramAuth = async (initData: string) => {
    setTelegramAuthInProgress(true);
    setLoading(true);
    try {
      const res = await authApi.telegramAuth(initData);
      const { token, user, group_ids, group_names, is_admin } = res.data.data;
      
      storeLogin({
        ...user,
        groupIds: group_ids || [],
        groupNames: group_names || [],
        permissions: [],
        isAdmin: is_admin || false,
      }, token);
      
      navigate('/');
    } catch (err: any) {
      console.error('Telegram auth failed:', err);
      const errorCode = err.response?.data?.error?.code;
      const errorMessage = err.response?.data?.error?.message;
      
      // Check error type
      if (errorCode === 'user_deleted' || errorMessage?.includes('deleted')) {
        setError(t('auth.user_deleted'));
      } else if (errorCode === 'user_not_registered' || errorMessage?.includes('not registered')) {
        setError(t('auth.user_not_registered'));
      } else if (errorMessage?.includes('status:')) {
        // Extract status from message or details
        const details = err.response?.data?.error?.details;
        const status = details?.status || 'unknown';
        setError(t('auth.user_status_error', { status: t(`users.status_${status}`, status) }));
      } else {
        setError(t('auth.telegram_auth_failed'));
      }
      setTelegramAuthInProgress(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authApi.login(form.login, form.password);
      const { token, user, group_ids, group_names, is_admin } = res.data.data;
      
      storeLogin({
        ...user,
        groupIds: group_ids || [],
        groupNames: group_names || [],
        permissions: [],
        isAdmin: is_admin || false,
      }, token);
      
      navigate('/');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message;
      if (errorMessage?.includes('Invalid credentials')) {
        setError(t('auth.invalid_credentials'));
      } else {
        setError(errorMessage || t('auth.login_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if running in Telegram WebApp
  const isTelegram = !!(window as { Telegram?: { WebApp?: { initData?: string } } }).Telegram?.WebApp?.initData;

  // Show loading while Telegram auth in progress
  if (telegramAuthInProgress && !error) {
    return (
      <PageContainer width="auth" py="xl">
        <Center style={{ minHeight: '50vh', flexDirection: 'column' }}>
          <Loader size="lg" mb="md" />
          <Text>{t('auth.authenticating')}</Text>
        </Center>
      </PageContainer>
    );
  }

  // In Telegram - show only error message, no login form
  if (isTelegram) {
    return (
      <PageContainer width="auth" py="xl">
        <Title ta="center" mb="lg">{t('auth.nre_login')}</Title>
        <Paper withBorder shadow="md" p={30} radius="md">
          {error ? (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {error}
            </Alert>
          ) : (
            <Center>
              <Loader size="lg" />
            </Center>
          )}
        </Paper>
      </PageContainer>
    );
  }

  // Web browser - show login form
  return (
    <PageContainer width="auth" py="xl">
      <Title ta="center" mb="lg">{t('auth.nre_login')}</Title>

      <Paper withBorder shadow="md" p={30} radius="md">
        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack>
            <TextInput
              label={t('auth.username')}
              placeholder={t('auth.login_placeholder')}
              value={form.login}
              onChange={(e) => setForm({ ...form, login: e.target.value })}
              required
              autoComplete="username"
            />

            <PasswordInput
              label={t('auth.password')}
              placeholder={t('auth.password_placeholder')}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              required
              autoComplete="current-password"
            />

            <Button type="submit" fullWidth loading={loading}>
              {t('auth.sign_in')}
            </Button>
          </Stack>
        </form>
      </Paper>
    </PageContainer>
  );
}

export default LoginPage;
